/*
 * This file is part of the Companion project
 * Copyright (c) 2018 Bitfocus AS
 * Authors: William Viker <william@bitfocus.io>, Håkon Nessjøen <haakon@bitfocus.io>, Dorian Meid <meid@backstage.org>
 *
 * This program is free software.
 * You should have received a copy of the MIT licence as well as the Bitfocus
 * Individual Contributor License Agreement for companion along with
 * this program.
 *
 * You can be released from the requirements of the license by purchasing
 * a commercial license. Buying such a license is mandatory as soon as you
 * develop commercial activities involving the Companion software without
 * disclosing the source code of your own applications.
 *
 */
import { DEFAULT_FONTS, DEFAULT_FONTS_STR } from './Fonts.js';
import { computeTextLayout, MIN_FONT_SIZE_FRACTION, resolveFontSizes, segmentTextToUnicodeChars, } from './TextParser.js';
/**
 * A simple image pool, to allow for using temporary images for compositing purposes
 * This should minimise GC pressure by reusing the same image object
 */
export class ImagePoolBase {
    #pool = [];
    async usingImage(textLayoutCache, fcn) {
        const image = this.#pool.pop() || this.createImage(textLayoutCache);
        try {
            image.clear();
            return await fcn(image);
        }
        finally {
            this.#pool.push(image);
        }
    }
}
/**
 * Class for generating an image and rendering some content to it
 */
export class ImageBase {
    logger;
    #imagePool;
    context2d;
    width;
    height;
    #textLayoutCache;
    /**
     * Create an image
     * @param width the width of the image in integer
     * @param height the height of the image in integer
     * @param oversampling a factor of how much more pixels the image should have in width and height
     */
    constructor(logger, pool, context2d, width, height, textLayoutCache) {
        this.logger = logger;
        this.#imagePool = pool;
        this.context2d = context2d;
        this.width = width;
        this.height = height;
        this.#textLayoutCache = textLayoutCache;
    }
    /**
     * Draw to a temporary image layer, before compositing it to the main image with a given alpha
     * This allows for complex drawing sequences to be flattened with the correct combined alpha
     */
    async usingTemporaryLayer(compositeAlpha, fcn) {
        return this.#imagePool.usingImage(this.#textLayoutCache, async (img) => {
            await fcn(img);
            await this.usingAlpha(compositeAlpha, async () => {
                this.drawImage(img.canvasImage, 0, 0, img.canvasImage.width, img.canvasImage.height, 0, 0, this.width, this.height);
            });
        });
    }
    /**
     * Perform some drawing with a given alpha.
     * Note: This affects the whole canvas drawing operations, it should contain a single operation otherwise the composition of each draw will not correctly combine colours
     */
    async usingAlpha(alpha, fcn) {
        const oldAlpha = this.context2d.globalAlpha;
        this.context2d.globalAlpha = alpha;
        try {
            await fcn();
        }
        finally {
            this.context2d.globalAlpha = oldAlpha;
        }
    }
    /**
     * Perform some drawing with a given rotation about the midpoint of the drawBounds.
     */
    async usingRotation(drawBounds, rotation, fcn) {
        this.context2d.save();
        const centerX = drawBounds.x + drawBounds.width / 2;
        const centerY = drawBounds.y + drawBounds.height / 2;
        this.context2d.translate(centerX, centerY);
        this.context2d.rotate((rotation * Math.PI) / 180);
        this.context2d.translate(-centerX, -centerY);
        try {
            await fcn();
        }
        finally {
            this.context2d.restore();
        }
    }
    /**
     * Clear the image, resetting to a fully transparent and empty state
     */
    clear() {
        this.context2d.clearRect(0, 0, this.width, this.height);
    }
    /**
     * fills the whole image with a color
     * @param color CSS color string
     * @returns success
     */
    fillColor(color) {
        return this.box(0, 0, this.width, this.height, color);
    }
    /**
     * draws a line between two given points
     */
    line(x1, y1, x2, y2, style) {
        this.context2d.lineWidth = style.width ?? 1;
        this.context2d.strokeStyle = style.color;
        this.context2d.beginPath();
        this.context2d.moveTo(x1, y1);
        this.context2d.lineTo(x2, y2);
        this.context2d.closePath();
        this.context2d.stroke();
    }
    /**
     * draws a horizontal line at given height from top
     */
    horizontalLine(y, style) {
        return this.line(0, y, this.width, y, style);
    }
    /**
     * draws a vertical line at given distance from left
     * @returns success
     */
    verticalLine(x, style) {
        return this.line(x, 0, x, this.height, style);
    }
    /**
     * draws a box with optional fill color and optional outline
     * @param x1
     * @param y1
     * @param x2
     * @param y2
     * @param color CSS string fill color, unfilled if undefined
     * @param strokeColor CSS string line color, no line if undefined
     * @param lineWidth line width defaults to 1
     * @param lineOrientation defaults to 'inside'
     * @returns something has been drawn
     */
    box(x1, y1, x2, y2, fillColor, lineStyle, lineOrientation = 'inside') {
        if (x2 == x1 || y2 == y1)
            return false;
        let didDraw = false;
        if (fillColor) {
            this.context2d.fillStyle = fillColor;
            this.context2d.fillRect(x1, y1, x2 - x1, y2 - y1);
            didDraw = true;
        }
        if (lineStyle) {
            didDraw = this.boxLine(x1, y1, x2, y2, lineStyle, lineOrientation) || didDraw;
        }
        return didDraw;
    }
    /**
     * draws a circle with optional fill color and optional outline
     * @param x
     * @param y
     * @param radiusX
     * @param radiusY
     * @param startAngle in radians
     * @param endAngle in radians
     * @param color CSS string fill color, unfilled if undefined
     * @param strokeColor CSS string line color, no line if undefined
     * @param lineWidth line width defaults to 1
     * @param lineOrientation defaults to 'inside'
     * @returns something has been drawn
     */
    circle(x, y, radiusX, radiusY, startAngle = 0, endAngle = Math.PI * 2, drawSlice, fillColor, lineStyle, borderOnlyArc, lineOrientation = 'inside') {
        if (radiusX <= 0 || radiusY <= 0)
            return false;
        // Special case for full circle, it doesn't draw if both points are the same and non-default
        if (Math.abs(startAngle - endAngle) <= 0.001) {
            startAngle = 0;
            endAngle = 2 * Math.PI;
            drawSlice = false;
        }
        let didDraw = false;
        if (fillColor) {
            this.context2d.fillStyle = fillColor;
            // this.context2d.fillRect(x1, y1, x2 - x1, y2 - y1)
            this.context2d.beginPath();
            this.context2d.ellipse(x, y, radiusX, radiusY, 0, startAngle, endAngle);
            if (drawSlice) {
                // Draw as a pie slice, connecting to center
                this.context2d.lineTo(x, y);
                this.context2d.closePath();
            }
            this.context2d.fill();
            didDraw = true;
        }
        if (lineStyle) {
            let borderRadiusX = radiusX;
            let borderRadiusY = radiusY;
            const lineWidth = lineStyle.width ?? 1;
            if (lineWidth > 0) {
                // Offset the radius to account for line position
                const halfline = lineWidth / 2;
                switch (lineOrientation) {
                    case 'inside':
                        borderRadiusX -= halfline;
                        borderRadiusY -= halfline;
                        break;
                    case 'outside':
                        borderRadiusX += halfline;
                        borderRadiusY += halfline;
                        break;
                }
                this.context2d.lineWidth = lineWidth;
                this.context2d.strokeStyle = lineStyle.color;
                this.context2d.beginPath();
                this.context2d.ellipse(x, y, borderRadiusX, borderRadiusY, 0, startAngle, endAngle);
                if (!borderOnlyArc) {
                    if (drawSlice) {
                        // Draw as a pie slice, connecting to center
                        this.context2d.lineTo(x, y);
                    }
                    this.context2d.closePath();
                }
                this.context2d.stroke();
                didDraw = true;
            }
        }
        return didDraw;
    }
    /**
     * Draws an outline rectangle
     * @param x1 position of left edge
     * @param y1 position of top edge
     * @param x2 position of right edge
     * @param y2 position of bottom edge
     * @param color color string
     * @param lineWidth line width
     * @param  lineOrientation direction of lines in regard to the edges
     * @returns returns true if a visible rectangle has been drawn
     */
    boxLine(x1, y1, x2, y2, lineStyle, lineOrientation = 'inside') {
        const lineWidth = lineStyle.width ?? 1;
        if (lineWidth <= 0)
            return false;
        const halfline = lineWidth / 2;
        switch (lineOrientation) {
            case 'inside':
                x1 += halfline;
                y1 += halfline;
                x2 -= halfline;
                y2 -= halfline;
                break;
            case 'outside':
                x1 -= halfline;
                y1 -= halfline;
                x2 += halfline;
                y2 += halfline;
                break;
        }
        this.context2d.lineWidth = lineWidth;
        this.context2d.strokeStyle = lineStyle.color;
        this.context2d.strokeRect(x1, y1, x2 - x1, y2 - y1);
        return true;
    }
    /**
     * Draws an image to the canvas from a buffer
     * the image can be fitted and cropped to the canvas in many variations
     * @param data buffer with the raw image data
     * @param xStart left position where to place the image
     * @param yStart top position where to place the image
     * @param width width of the bounding box where to place the image
     * @param height height of the bounding box where to place the image
     * @param halign horizontal alignment of the image in the bounding box (defaults to center)
     * @param valign vertical alignment of the image in the bounding box (defaults to center)
     * @param scale the size factor of the image. Number scales by specified amount, fill scales to fill the bounding box neglecting aspect ratio, crop scales to fill the bounding box and crop if necessary, fit scales to fit the bounding box with the longer side
     */
    async drawBase64Image(base64Image, xStart = 0, yStart = 0, width = 72, height = 72, halign = 'center', valign = 'center', scale = 1) {
        if (!base64Image || base64Image.length <= 40) {
            // No image data. This is a bit cautious of a threshold, as empty buffers cause the canvas to crash
            return;
        }
        let canvasImage;
        try {
            canvasImage = await this.loadBase64Image(base64Image);
        }
        catch (e) {
            this.logger.error('Error loading image', e);
            return;
        }
        const imageWidth = canvasImage.width;
        const imageHeight = canvasImage.height;
        let calculatedScale = 1;
        let scaledImageWidth = imageWidth;
        let scaledImageHeight = imageHeight;
        if (scale === 'fit_or_shrink') {
            if (imageWidth <= width && imageHeight <= height) {
                // If image is smaller than the button, don't scale it
                scale = 1;
            }
            else {
                // Otherwise shrink to fit
                scale = 'fit';
            }
        }
        if (scale === 'fit') {
            const scaleMin = Math.min(width / imageWidth, height / imageHeight);
            const scaleMax = Math.max(width / imageWidth, height / imageHeight);
            calculatedScale = scaleMax < scaleMin ? scaleMax : scaleMin;
            scaledImageWidth = imageWidth * calculatedScale;
            scaledImageHeight = imageHeight * calculatedScale;
        }
        else if (scale === 'crop') {
            calculatedScale = Math.max(width / imageWidth, height / imageHeight);
            scaledImageWidth = imageWidth * calculatedScale;
            scaledImageHeight = imageHeight * calculatedScale;
        }
        else if (typeof scale === 'number') {
            if (scale === 0) {
                this.logger.warn('image scale is zero, abort drawing of image');
                return;
            }
            calculatedScale = scale;
            scaledImageWidth = imageWidth * calculatedScale;
            scaledImageHeight = imageHeight * calculatedScale;
        }
        else {
            // if there is none of the aspect ration retaining scales, do the 'fill'-type
            scaledImageWidth = width;
            scaledImageHeight = height;
        }
        if (scaledImageWidth < 1 || scaledImageHeight < 1) {
            this.logger.warn('image width or height after scaling is less then one pixel, abort drawing of image');
            return;
        }
        // set default transformation values at 'fill'-type
        const source = { x: 0, y: 0, w: imageWidth, h: imageHeight };
        const destination = { x: xStart, y: yStart, w: width, h: height };
        if (scaledImageWidth > width) {
            //image is broader than drawing pane
            source.w = width / calculatedScale;
            switch (halign) {
                case 'center':
                    source.x = (imageWidth - source.w) / 2;
                    break;
                case 'right':
                    source.x = imageWidth - source.w;
                    break;
            }
        }
        else if (scaledImageWidth < width) {
            // image is narrower than drawing pane
            destination.w = scaledImageWidth;
            switch (halign) {
                case 'center':
                    destination.x += (width - scaledImageWidth) / 2;
                    break;
                case 'right':
                    destination.x += width - scaledImageWidth;
                    break;
            }
        }
        if (scaledImageHeight > height) {
            // image is taller than drawing pane
            source.h = height / calculatedScale;
            switch (valign) {
                case 'center':
                    source.y = (imageHeight - source.h) / 2;
                    break;
                case 'bottom':
                    source.y = imageHeight - source.h;
                    break;
            }
        }
        else if (scaledImageHeight < height) {
            // image is smaller than drawing pane
            destination.h = scaledImageHeight;
            switch (valign) {
                case 'center':
                    destination.y += (height - scaledImageHeight) / 2;
                    break;
                case 'bottom':
                    destination.y += height - scaledImageHeight;
                    break;
            }
        }
        this.drawImage(canvasImage, source.x, source.y, source.w, source.h, destination.x, destination.y, destination.w, destination.h);
    }
    #sanitiseText(text) {
        if (text === undefined)
            return '';
        // If there is a null character in the string, cut it off
        const nullIndex = text.indexOf('\0');
        if (nullIndex == -1)
            return text;
        return text.substring(0, nullIndex);
    }
    /**
     * draws a single line of left aligned text
     * the line length is not wrapped or limited and may extend beyond the canvas
     * @param x left position where to start the line
     * @param y top position where to start the line
     * @param text
     * @param color CSS color string
     * @param fontsize Em height
     * @param dummy Don't actually draw anything just return the text width
     * @returns width of the line
     */
    drawTextLine(x, y, text, color, fontsize, dummy = false) {
        text = this.#sanitiseText(text);
        if (text === undefined || text.length == 0)
            return 0;
        if (isNaN(fontsize))
            return 0;
        if (fontsize < 3)
            return 0;
        this.context2d.font = `${fontsize}px ${DEFAULT_FONTS_STR}`;
        const metrics = this.context2d.measureText(text);
        if (!dummy) {
            this.context2d.textAlign = 'left';
            this.context2d.fillStyle = color;
            this.context2d.fillText(text, x, y + Math.round(metrics.fontBoundingBoxAscent));
        }
        return metrics.width;
    }
    /**
     * draws a single line of aligned text, doesn't care for line breaks or length
     * @param x horizontal value of the alignment point
     * @param y vertical value of the alignment point
     * @param text
     * @param color CSS color string
     * @param fontsize Em height
     * @param halignment defaults to 'center'
     * @param valignment defaults to 'center'
     * @returns the width of the line
     */
    drawTextLineAligned(x, y, text, color, fontsize, halignment = 'center', valignment = 'center', weight = 'normal') {
        text = this.#sanitiseText(text);
        if (text === undefined || text.length == 0)
            return 0;
        if (halignment != 'left' && halignment != 'center' && halignment != 'right')
            halignment = 'left';
        if (weight != 'normal' && weight != 'bold')
            weight = 'normal';
        this.context2d.font = `${weight} ${fontsize}px ${DEFAULT_FONTS_STR}`;
        this.context2d.fillStyle = color;
        this.context2d.textAlign = halignment;
        const metrics = this.context2d.measureText(text);
        let vOffset = 0;
        switch (valignment) {
            case 'top':
                vOffset = metrics.fontBoundingBoxAscent;
                break;
            case 'center':
                vOffset = metrics.fontBoundingBoxAscent / 2;
                break;
            case 'bottom':
                vOffset = metrics.fontBoundingBoxDescent * -1;
                break;
        }
        this.context2d.fillText(text, x, y + vOffset);
        return metrics.width;
    }
    /**
     * Draws aligned text in an boxed area.
     * @param x bounding box top left horizontal value
     * @param y bounding box top left vertical value
     * @param w bounding box width
     * @param h bounding box height
     * @param text the text to draw
     * @param color CSS color string
     * @param fontsize height of font, either pixels or 'auto'
     * @param halign horizontal alignment left, center, right
     * @param valign vertical alignment top, center, bottom
     */
    drawAlignedText(x, y, w, h, text, color, fontsize = 'auto', halign = 'center', valign = 'center', outlineStyle) {
        let displayTextStr = this.#sanitiseText(text).toString().trim(); // remove leading and trailing spaces for display
        if (!displayTextStr)
            return;
        displayTextStr = displayTextStr.replaceAll('\r\n', '\n'); // we only want \n as a linebreak
        displayTextStr = displayTextStr.replaceAll('\\n', '\n'); // users can add deliberate line breaks, let's replace it with a real line break
        displayTextStr = displayTextStr.replaceAll('\\r', '\n'); // users can add deliberate line breaks, let's replace it with a real line break
        displayTextStr = displayTextStr.replaceAll('\\t', '\t'); // users can add deliberate tabs, let's replace it with a real tab
        // Estimate character limit based on minimum font size (7px) with 1px char width and 2px height
        // This gives a conservative upper bound on how many characters could possibly fit
        const maxPossibleChars = Math.floor((w * h) / (1 * 2));
        const { displayTextChars, displayTextCharsStr, wasTruncated } = segmentTextToUnicodeChars(displayTextStr, maxPossibleChars);
        // If we hit the character limit, only the smallest font size could possibly fit
        const checkSizes = wasTruncated
            ? [Math.max(MIN_FONT_SIZE_FRACTION * h, 1)]
            : resolveFontSizes(w, h, fontsize, displayTextChars.length);
        // Find the best fitting size
        let textLayout;
        for (const size of checkSizes) {
            const fontLineHeight = size * 1.1; // this lineheight is not the real lineheight needed for the font, but it is calculated to match the existing font size / lineheight ratio of the bitmap fonts
            const fontSpec = `${size}px/${fontLineHeight}px ${DEFAULT_FONTS}`;
            // Check cache first
            const cacheKey = `${fontSpec}:${w}:${h}:${displayTextCharsStr}`;
            textLayout = this.#textLayoutCache?.get(cacheKey);
            if (!textLayout) {
                textLayout = computeTextLayout(this.context2d, w, h, displayTextChars, fontSpec);
                // Store in cache
                this.#textLayoutCache?.set(cacheKey, textLayout);
            }
            if (textLayout.fits)
                break;
        }
        // Perform the draw
        this.#drawTextLayout(x, y, w, h, textLayout, color, halign, valign, outlineStyle);
    }
    /**
     * Draw text using a computed layout
     */
    #drawTextLayout(x, y, w, h, layout, color, halign, valign, outlineStyle) {
        if (layout.lines.length < 1)
            return;
        // Set font for rendering
        this.context2d.font = layout.fontDefinition;
        let xAnchor = x;
        switch (halign) {
            case 'left':
                this.context2d.textAlign = 'left';
                xAnchor = x;
                break;
            case 'center':
                this.context2d.textAlign = 'center';
                xAnchor = x + w / 2;
                break;
            case 'right':
                this.context2d.textAlign = 'right';
                xAnchor = x + w;
                break;
        }
        const linesTotalHeight = layout.lines.length * layout.measuredLineHeight;
        let yAnchor = 0;
        switch (valign) {
            case 'top':
                yAnchor = layout.measuredAscent;
                break;
            case 'center':
                yAnchor = (h - linesTotalHeight) / 2 + layout.measuredAscent;
                break;
            case 'bottom':
                yAnchor = h - linesTotalHeight + layout.measuredAscent;
                break;
        }
        yAnchor += y;
        this.context2d.fillStyle = color;
        if (outlineStyle && outlineStyle.width > 0) {
            this.context2d.strokeStyle = outlineStyle.color;
            this.context2d.lineWidth = outlineStyle.width;
        }
        for (const line of layout.lines) {
            if (outlineStyle && outlineStyle.width > 0) {
                this.context2d.strokeText(line.text, xAnchor, yAnchor);
            }
            this.context2d.fillText(line.text, xAnchor, yAnchor);
            //this.horizontalLine(yAnchor - fontsize, 'rgb(255,0,255)')
            // this.horizontalLine(yAnchor + correctedDescent, 'rgb(0, 255, 0)')
            // this.horizontalLine(yAnchor + line.descent, 'rgb(0, 255, 0)')
            // this.horizontalLine(yAnchor - correctedAscent, 'rgb(0,0,255)')
            // this.horizontalLine(yAnchor, 'rgb(255, 0, 0)')
            //console.log('Fontsize', fontsize, 'Lineheight', lineheight, 'asc', correctedAscent, 'des', correctedDescent, 'a+d', correctedAscent+correctedDescent);
            yAnchor += layout.measuredLineHeight;
        }
    }
    /**
     * drawPixeBuffer
     *
     * Buffer can be either a buffer, or base64 encoded string.
     * Type can be set to either 'buffer' or 'base64' according to your input data.
     * Width and height is information about your buffer, not scaling.
     *
     * The buffer data is expected to be RGB or ARGB data, 1 byte per color,
     * horizontally. Top left is first three bytes.
     *
     * @param x left position of insert point
     * @param y top position of insert point
     * @param width integer information of the width of the buffer
     * @param height integer information of the height of the buffer
     * @param bufferRaw
     * @param format pixel format of the buffer, if known
     * @param scale scaling factor
     */
    drawPixelBuffer(x, y, width, height, bufferRaw, format, scale) {
        let buffer;
        if (typeof bufferRaw == 'object') {
            if (bufferRaw instanceof Buffer) {
                buffer = bufferRaw;
            }
            else if (bufferRaw instanceof Uint8Array) {
                buffer = Buffer.from(bufferRaw.buffer, bufferRaw.byteOffset, bufferRaw.byteLength);
            }
            else {
                this.logger.error(`Pixelbuffer is of unknown type (${typeof bufferRaw})`);
                return;
            }
        }
        else if (typeof bufferRaw == 'string') {
            buffer = Buffer.from(bufferRaw, 'base64');
        }
        else {
            this.logger.error(`Pixelbuffer is of unknown type (${typeof bufferRaw})`);
            return;
        }
        const rgbByteCount = width * height * 3;
        const rgbaByteCount = width * height * 4;
        if (format == 'RGB' && buffer.length !== rgbByteCount) {
            this.logger.error(`Pixelbuffer of format ${format} with ${buffer.length} bytes is not expected ${rgbByteCount} bytes`);
            return;
        }
        else if ((format == 'RGBA' || format == 'ARGB') && buffer.length !== rgbaByteCount) {
            this.logger.error(`Pixelbuffer of format ${format} with ${buffer.length} bytes is not expected ${rgbaByteCount} bytes`);
            return;
        }
        else if (!format && buffer.length < rgbByteCount) {
            this.logger.error(`Pixelbuffer of ${buffer.length} bytes is not expected ${rgbByteCount} bytes`);
            return;
        }
        if (buffer.length == rgbaByteCount && format === 'RGBA') {
            // Buffer is packed ok
        }
        else if (buffer.length == rgbaByteCount && (!format || format === 'ARGB')) {
            // ARGB: swap order from ARGB to RGBA
            const argb = Uint8Array.from(buffer);
            buffer = Buffer.alloc(width * height * 4);
            for (let i = 0; i < buffer.length; i += 4) {
                const a = argb[i];
                buffer[i] = argb[i + 1];
                buffer[i + 1] = argb[i + 2];
                buffer[i + 2] = argb[i + 3];
                buffer[i + 3] = a;
            }
        }
        else if (buffer.length == rgbByteCount) {
            // RGB: add alpha channel
            const rgb = Uint8Array.from(buffer);
            buffer = Buffer.alloc(width * height * 4);
            for (let i = 0; i < rgb.length / 3; i += 1) {
                buffer[i * 4] = rgb[i * 3]; // Red
                buffer[i * 4 + 1] = rgb[i * 3 + 1]; // Green
                buffer[i * 4 + 2] = rgb[i * 3 + 2]; // Blue
                buffer[i * 4 + 3] = 255; // Alpha
            }
        }
        else {
            this.logger.error(`Pixelbuffer for a ${width}x${height} image should be either ${rgbByteCount} or ${rgbaByteCount} bytes big. Not ${buffer.length} (${format})`);
            return;
        }
        // create HTML compatible imageData object
        const imageCanvas = this.loadPixelBuffer(buffer, width, height);
        if (!imageCanvas)
            return;
        if (!scale)
            scale = 1;
        this.drawImage(imageCanvas, 0, 0, width, height, x, y, width * scale, height * scale);
    }
    /**
     * Draws a triangle in a corner
     * @param depth
     * @param color CSS color string
     * @param halign defaults to 'left'
     * @param valign defaults to 'top'
     * @returns success
     */
    /*
    drawCornerTriangle(
        depth: number,
        color: string,
        halign: HorizontalAlignment = 'left',
        valign: VerticalAlignment = 'top'
    ): boolean {
        if (depth == 0) return false
        let points: PointXY[] | undefined

        if (halign == 'left' && valign == 'top') {
            points = [
                [0, 0],
                [depth, 0],
                [0, depth],
            ]
        }
        if (halign == 'right' && valign == 'top') {
            points = [
                [this.width, 0],
                [this.width - depth, 0],
                [this.width, depth],
            ]
        }
        if (halign == 'right' && valign == 'bottom') {
            points = [
                [this.width, this.height],
                [this.width - depth, this.height],
                [this.width, this.height - depth],
            ]
        }
        if (halign == 'left' && valign == 'bottom') {
            points = [
                [0, this.height],
                [depth, this.height],
                [0, this.height - depth],
            ]
        }
        if (!points) return false

        return this.drawFilledPath(points, color)
    }
        */
    /**
     * Draws an outline path from some points
     * @param pathPoints an array of x and y points
     * @param color CSS color
     * @param lineWidth defaults to 1
     * @param close if true close the path from last point to first point, if last and first point are identical path will be autoclosed
     * @returns success
     */
    drawPath(pathPoints, lineStyle, close = false) {
        const lineWidth = lineStyle.width ?? 1;
        if (lineWidth <= 0)
            return false;
        if (!Array.isArray(pathPoints) || pathPoints.length == 0)
            return false;
        this.context2d.beginPath();
        const [firstPoint, ...points] = pathPoints;
        this.context2d.moveTo(firstPoint[0], firstPoint[1]);
        points.forEach((point) => {
            this.context2d.lineTo(point[0], point[1]);
        });
        const lastPoint = points[points.length - 1];
        if (close || (lastPoint && firstPoint[0] == lastPoint[0] && firstPoint[1] == lastPoint[1])) {
            this.context2d.closePath();
        }
        this.context2d.strokeStyle = lineStyle.color;
        this.context2d.lineWidth = lineWidth;
        this.context2d.stroke();
        return true;
    }
    /**
     * Draws a filled path from some points
     * @param pathPoints an array of x and y points
     * @param color CSS color
     * @returns success
     */
    drawFilledPath(pathPoints, color) {
        if (!Array.isArray(pathPoints) || pathPoints.length == 0)
            return false;
        this.context2d.beginPath();
        const [firstPoint, ...points] = pathPoints;
        this.context2d.moveTo(firstPoint[0], firstPoint[1]);
        points.forEach((point) => {
            this.context2d.lineTo(point[0], point[1]);
        });
        this.context2d.fillStyle = color;
        this.context2d.fill();
        return true;
    }
}
//# sourceMappingURL=ImageBase.js.map